import { useTranslation } from 'react-i18next';
import LocaleLink from '@/components/LocaleLink';
import { usePlatformMetrics } from '@/hooks/usePlatformMetrics';

/**
 * Proof strip (Messaging Architecture v1.0, homepage §2).
 *
 * This replaces the unattributed percentages that used to sit here. Every
 * number is checkable in the platform and every one links to the page where a
 * buyer can check it — verifiability is only useful if it is one click away.
 *
 * Numbers come from the live metrics feed via usePlatformMetrics, which falls
 * back to the compiled seed in src/config/platform-metrics.ts. Do not hardcode
 * them here; that is how the site ended up claiming "111 jurisdictions", a
 * figure that matched nothing in the database. The figures now move when the
 * platform moves, without a release.
 *
 * "27 regulatory domains" was deliberately dropped rather than restated: the
 * platform holds 15, and an unsourced number in a strip whose whole purpose is
 * verifiability defeats the section.
 *
 * MESSAGING ARCHITECTURE v1.1
 * Citation depth leads, because depth beats breadth above the fold. One number
 * that says every requirement traces to the instrument that created it is worth
 * more to this buyer than a bigger number that says how many things we list,
 * and it is the claim a competitor on breadth cannot answer.
 *
 * The connector count moved to /integrations. That is a positioning call rather
 * than a comment on the figure: connector totals invite a straight breadth
 * comparison, which is the axis v1.1 deliberately stops competing on. The
 * 116/39 figures are a compiled snapshot, unpublished from the live feed,
 * because nothing writes those `pushed` rows. Do not wire
 * `proofStrip.integrations` to the hook as if it updates.
 *
 * Back to the single dot-separated row it was, at the same size, with the
 * figures bolded. It had been broken out into a four-column stat block, which
 * gave it more weight than a rail under the hero should carry. The value/label
 * split from that version stays useful here: the number is bold, the words are
 * not, which is the whole of the emphasis.
 */
const ProofStrip = () => {
  const { t } = useTranslation();
  const m = usePlatformMetrics();

  const items = [
    {
      to: '/global-coverage',
      value: m.cited_requirements.toLocaleString('en-US'),
      label: t('proofStrip.citationsLabel'),
    },
    {
      to: '/global-coverage',
      value: `${m.frameworks_in_picker}+`,
      label: t('proofStrip.frameworksLabel'),
    },
    {
      to: '/global-coverage',
      value: String(m.regulatory_data_jurisdictions),
      label: t('proofStrip.jurisdictionsLabel'),
    },
    {
      to: '/global-coverage',
      value: t('proofStrip.regGenomeValue'),
      label: t('proofStrip.regGenomeLabel'),
    },
  ];

  return (
    <section
      // The hero measures this strip so it can size itself to leave it on the
      // first screen. See the fit-to-fold effect in VibeCompliance.tsx.
      data-hero-strip
      aria-label={t('proofStrip.aria')}
      className="border-b border-border bg-muted/30"
    >
      <div className="container mx-auto max-w-6xl px-6 py-4">
        <ul className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm text-muted-foreground">
          {items.map((item, idx) => (
            <li key={item.label} className="flex items-center gap-3">
              {idx > 0 && <span aria-hidden className="text-border">·</span>}
              <LocaleLink to={item.to} className="hover:text-foreground hover:underline">
                <span className="font-semibold text-foreground">{item.value}</span>{' '}
                {item.label}
              </LocaleLink>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default ProofStrip;
